import React, { useMemo, useState } from 'react';

import {
  Button,
  createStyles,
  Divider,
  Group,
  Loader,
  Popover,
  Text,
  TextInput,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconChevronDown,
  IconClock,
  IconLink,
  IconPencil,
  IconPlus,
  IconSearch,
  IconX,
} from '@tabler/icons-react';
import useWorkoutBenchmarks from 'hooks/workoutBenchmark/useWorkoutBenchmarks';
import { SCORE_TYPE_LABEL_MAP, ScoreTypeEnum } from 'types/enums';

import { SelectedBenchmark } from '../../../../../forms/workoutBuilder/types';

interface ILinkBenchmarkControlProps {
  linkedBenchmark: { id: string; name: string } | null;
  /** Section will be saved as a new benchmark (deferred to workout-save) — shown as a "will be created" pill. */
  pendingBenchmarkName?: string | null;
  onLink: (benchmark: SelectedBenchmark) => void;
  onUnlink: () => void;
  onEditPending?: () => void;
  onRemovePending?: () => void;
  /** Open the Create Benchmark modal to make a new library benchmark and link it. */
  onCreateNew?: () => void;
  disabled?: boolean;
  dataCy?: string;
}

const useStyles = createStyles(theme => ({
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.md,
    padding: '8px 12px',
    borderRadius: theme.radius.sm,
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.colors.gray[0] },
  },
}));

const LinkBenchmarkControl = ({
  linkedBenchmark,
  pendingBenchmarkName,
  onLink,
  onUnlink,
  onEditPending,
  onRemovePending,
  onCreateNew,
  disabled = false,
  dataCy,
}: ILinkBenchmarkControlProps): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data, isLoading } = useWorkoutBenchmarks({ search: debouncedSearch });
  const benchmarks = useMemo(() => (data?.pages ?? []).flatMap(page => page.data.results), [data]);

  if (linkedBenchmark) {
    return (
      <Button
        component='div'
        variant='default'
        leftIcon={<IconLink size={16} color={theme.colors.blue[7]} />}
        rightIcon={
          disabled ? undefined : (
            <UnstyledButton
              onClick={onUnlink}
              aria-label={`Unlink ${linkedBenchmark.name}`}
              data-cy={dataCy ? `${dataCy}-unlink` : undefined}
              sx={{
                display: 'flex',
                color: theme.colors.gray[6],
                '&:hover': { color: theme.colors.dark[6] },
              }}
            >
              <IconX size={14} />
            </UnstyledButton>
          )
        }
        styles={{
          root: {
            backgroundColor: theme.colors.blue[0],
            borderColor: theme.colors.blue[2],
            color: theme.colors.blue[7],
            cursor: 'default',
            '&:hover': { backgroundColor: theme.colors.blue[0] },
          },
        }}
      >
        {linkedBenchmark.name}
      </Button>
    );
  }

  if (pendingBenchmarkName) {
    return (
      <Button
        component='div'
        variant='default'
        leftIcon={<IconClock size={16} color={theme.colors.blue[5]} />}
        rightIcon={
          disabled ? undefined : (
            <Group spacing={4} noWrap>
              <UnstyledButton
                onClick={onEditPending}
                aria-label={`Edit benchmark ${pendingBenchmarkName}`}
                data-cy={dataCy ? `${dataCy}-edit-pending` : undefined}
                sx={{
                  display: 'flex',
                  color: theme.colors.gray[6],
                  '&:hover': { color: theme.colors.dark[6] },
                }}
              >
                <IconPencil size={14} />
              </UnstyledButton>
              <UnstyledButton
                onClick={onRemovePending}
                aria-label={`Remove pending benchmark ${pendingBenchmarkName}`}
                data-cy={dataCy ? `${dataCy}-remove-pending` : undefined}
                sx={{
                  display: 'flex',
                  color: theme.colors.gray[6],
                  '&:hover': { color: theme.colors.dark[6] },
                }}
              >
                <IconX size={14} />
              </UnstyledButton>
            </Group>
          )
        }
        styles={{
          root: {
            borderStyle: 'dashed',
            borderColor: theme.colors.blue[2],
            color: theme.colors.dark[6],
            cursor: 'default',
          },
        }}
      >
        Will be created: {pendingBenchmarkName}
      </Button>
    );
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position='bottom-end'
      width={320}
      shadow='md'
      withinPortal
      trapFocus
    >
      <Popover.Target>
        <Button
          variant='default'
          leftIcon={<IconLink size={16} />}
          rightIcon={<IconChevronDown size={14} />}
          onClick={() => setOpened(o => !o)}
          disabled={disabled}
          aria-label='Link a benchmark'
          data-cy={dataCy}
          styles={{
            root: {
              borderStyle: 'dashed',
              borderColor: theme.colors.gray[4],
              color: theme.colors.gray[7],
              '&:hover': {
                backgroundColor: theme.white,
                borderColor: theme.colors.blue[3],
                color: theme.colors.blue[6],
              },
            },
          }}
        >
          Link Benchmark
        </Button>
      </Popover.Target>
      <Popover.Dropdown p='xs'>
        <TextInput
          placeholder='Search benchmarks...'
          icon={<IconSearch size={16} />}
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          mb='xs'
          data-cy={dataCy ? `${dataCy}-search` : undefined}
        />
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          {isLoading ? (
            <Group position='center' py='sm'>
              <Loader size={theme.fontSizes.md} />
            </Group>
          ) : benchmarks.length === 0 ? (
            <Text size='sm' color='dimmed' align='center' py='sm'>
              No benchmarks found
            </Text>
          ) : (
            benchmarks.map(b => (
              <div
                key={b.workoutMovementId}
                className={classes.row}
                onClick={() => {
                  onLink({
                    id: b.workoutMovementId,
                    name: b.name,
                    defaultScoreType: (b.defaultScoreType as ScoreTypeEnum | '' | null) || null,
                    description: b.description ?? null,
                  });
                  setOpened(false);
                  setSearch('');
                }}
              >
                <Text size='sm' weight={600}>
                  {b.name}
                </Text>
                {b.defaultScoreType && (
                  <Text size='sm' color='dimmed'>
                    {SCORE_TYPE_LABEL_MAP[b.defaultScoreType as ScoreTypeEnum]}
                  </Text>
                )}
              </div>
            ))
          )}
        </div>
        {onCreateNew && (
          <>
            <Divider my='xs' />
            <UnstyledButton
              onClick={() => {
                onCreateNew();
                setOpened(false);
                setSearch('');
              }}
              data-cy={dataCy ? `${dataCy}-create-new` : undefined}
              sx={{
                display: 'block',
                width: '100%',
                padding: '8px 12px',
                borderRadius: theme.radius.sm,
                '&:hover': { backgroundColor: theme.colors.gray[0] },
              }}
            >
              <Group spacing={8} noWrap>
                <IconPlus size={16} color={theme.colors.blue[6]} />
                <Text size='sm' weight={600} color={theme.colors.blue[6]}>
                  Create new benchmark
                </Text>
              </Group>
            </UnstyledButton>
          </>
        )}
      </Popover.Dropdown>
    </Popover>
  );
};

export default LinkBenchmarkControl;
